import posthog from 'posthog-js';
import { getClientEnvVariables } from '@/_utils/getEnvVariables.client';

/**
 * Thin wrapper over PostHog event capture.
 * All custom event names and property shapes are defined here
 * so tracking is consistent across the app.
 *
 * Every method is a no-op when PostHog is not initialized
 * (i.e. when NEXT_PUBLIC_POSTHOG_KEY is empty).
 */

function isActive(): boolean {
	return typeof window !== 'undefined' && !!getClientEnvVariables().NEXT_PUBLIC_POSTHOG_KEY;
}

export const AnalyticsService = {
	/** User completed registration / was provisioned */
	userSignedUp(properties?: { source?: string; referrer?: string }) {
		if (!isActive()) return;
		posthog.capture('user_signed_up', properties);
	},

	/** User submitted a query (human message) */
	querySubmitted(properties: {
		conversationId: string;
		queryLength: number;
		queryText: string;
		conversationDepth: number;
		isFirstQuery: boolean;
	}) {
		if (!isActive()) return;
		posthog.capture('query_submitted', {
			conversation_id: properties.conversationId,
			query_length: properties.queryLength,
			query_text: properties.queryText.slice(0, 500),
			conversation_depth: properties.conversationDepth,
			is_first_query: properties.isFirstQuery
		});
	},

	/** AI response stream completed */
	responseReceived(properties: {
		conversationId: string;
		messageId: string;
		hadCitations: boolean;
		citationCount: number;
		wasFallback: boolean;
		latencyMs: number;
		tokenCost?: number;
	}) {
		if (!isActive()) return;
		posthog.capture('response_received', {
			conversation_id: properties.conversationId,
			message_id: properties.messageId,
			had_citations: properties.hadCitations,
			citation_count: properties.citationCount,
			was_fallback: properties.wasFallback,
			latency_ms: properties.latencyMs,
			token_cost: properties.tokenCost
		});
	},

	/** User gave thumbs up/down feedback */
	feedbackGiven(properties: { conversationId: string; messageId: string; feedbackType: string }) {
		if (!isActive()) return;
		posthog.capture('feedback_given', {
			conversation_id: properties.conversationId,
			message_id: properties.messageId,
			feedback_type: properties.feedbackType
		});
	},

	/** User toggled conversation to public */
	conversationShared(properties: { conversationId: string }) {
		if (!isActive()) return;
		posthog.capture('conversation_shared', {
			conversation_id: properties.conversationId
		});
	},

	/** Someone viewed a publicly shared conversation */
	sharedConversationViewed(properties: {
		conversationId: string;
		conversationTitle: string;
		messageCount: number;
		citationCount: number;
	}) {
		if (!isActive()) return;
		posthog.capture('shared_conversation_viewed', {
			conversation_id: properties.conversationId,
			conversation_title: properties.conversationTitle.slice(0, 200),
			message_count: properties.messageCount,
			citation_count: properties.citationCount
		});
	}
};
